import { salesforceOpenCtiStatusAtom, store } from '@/lib/store';

const OPEN_CTI_SCRIPT_SRC =
  'https://hitcommunications4-dev-ed.develop.vf.force.com/support/api/61.0/lightning/opencti_min.js';

const OPEN_CTI_READY_TIMEOUT_MS = 30000;
const OPEN_CTI_READY_POLL_MS = 100;
const OPEN_CTI_RETRY_BASE_DELAY_MS = 250;
const OPEN_CTI_RETRY_MAX_DELAY_MS = 10000;
const OPEN_CTI_RECONNECTING_AFTER_FAILURES = 3;

let openCtiReadyPromise: Promise<void> | undefined;

const getOpenCti = () =>
  (globalThis as unknown as { sforce?: { opencti?: unknown } }).sforce?.opencti;

const findOpenCtiScript = () =>
  Array.from(document.scripts).find(
    (script) => script.src === OPEN_CTI_SCRIPT_SRC
  );

export const detectSalesforce = (): boolean => {
  try {
    const host = new URL(document.referrer).hostname;
    return (
      /\.my\.salesforce\.com$/.test(host) ||
      /\.salesforce\.com$/.test(host) ||
      /\.lightning\.force\.com$/.test(host) ||
      /\.visual\.force\.com$/.test(host)
    );
  } catch {
    return false;
  }
};

export const ensureSalesforceOpenCtiReady = (): Promise<void> => {
  if (getOpenCti()) {
    store.set(salesforceOpenCtiStatusAtom, 'ready');
    return Promise.resolve();
  }

  if (!openCtiReadyPromise) {
    store.set(salesforceOpenCtiStatusAtom, 'connecting');
    openCtiReadyPromise = (async () => {
      let failureCount = 0;

      try {
        while (true) {
          try {
            await waitForOpenCti();
            store.set(salesforceOpenCtiStatusAtom, 'ready');
            return;
          } catch {
            failureCount += 1;
            if (failureCount >= OPEN_CTI_RECONNECTING_AFTER_FAILURES) {
              store.set(salesforceOpenCtiStatusAtom, 'reconnecting');
            }

            const retryDelay = Math.min(
              OPEN_CTI_RETRY_BASE_DELAY_MS * 2 ** (failureCount - 1),
              OPEN_CTI_RETRY_MAX_DELAY_MS
            );
            await waitForRetry(retryDelay);
          }
        }
      } finally {
        openCtiReadyPromise = undefined;
      }
    })();
  }

  return openCtiReadyPromise;
};

const waitForRetry = (delayMs: number): Promise<void> =>
  new Promise<void>((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      window.removeEventListener('online', finish);
      resolve();
    };

    const timeout = window.setTimeout(finish, delayMs);
    window.addEventListener('online', finish);
  });

const waitForOpenCti = (): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    let settled = false;
    const cleanup: Array<() => void> = [];

    let script = findOpenCtiScript();
    const shouldAppendScript = !script;

    if (!script) {
      script = document.createElement('script');
      script.src = OPEN_CTI_SCRIPT_SRC;
      script.type = 'text/javascript';
      script.async = true;
    }

    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      cleanup.forEach((fn) => fn());

      if (error) {
        script.remove();
        reject(error);
        return;
      }

      resolve();
    };

    const checkReady = () => {
      if (getOpenCti()) finish();
    };

    const onLoad = () => checkReady();
    const onError = () => {
      finish(
        new Error(`Failed to load Salesforce Open CTI script: ${script.src}`)
      );
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    cleanup.push(() => script.removeEventListener('load', onLoad));
    cleanup.push(() => script.removeEventListener('error', onError));

    const timeout = window.setTimeout(() => {
      finish(
        new Error(
          `Salesforce Open CTI did not become ready after ${OPEN_CTI_READY_TIMEOUT_MS}ms`
        )
      );
    }, OPEN_CTI_READY_TIMEOUT_MS);
    cleanup.push(() => window.clearTimeout(timeout));

    const interval = window.setInterval(checkReady, OPEN_CTI_READY_POLL_MS);
    cleanup.push(() => window.clearInterval(interval));

    if (shouldAppendScript) document.head.appendChild(script);
    checkReady();
  });
